import { Col, Dropdown, Modal, Row, Space, Switch, Typography, message } from "antd";
import EmployeesView from "../components/EmployeesView";
import { SearchQuery } from "../code/search-query/search-query";
import { useState } from "react";
import { Guid } from "guid-typescript";
import Loader from "../controls/Loader";
import {
  getArchivedEmployees,
  getCompanyEmployeesExport,
  restoreEmployee,
} from "../repository/data.repository";
import ArchivedListView from "../controls/ArchivedListView";
import RegisterEmployeesWizard from "../components/RegisterEmployeesWizard";
import { EmployeeUser } from "../repository/data.default";
import EmployeeView from "../components/EmployeeView";
import { EmployeeViewForm } from "../components/EmployeeView.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function EmployeesPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [registerIsOpen, setRegisterIsOpen] = useState(false);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [userInputMode, setUserInputMode] = useState<"delete" | "view">(
    "delete",
  );
  const [userInputUid, setUserInputUid] = useState<string | null | undefined>(
    {} as any,
  );
  const [userInputOpen, setUserInputOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onEmployeeInputOpen(
    mode: "view" | "delete",
    values?: EmployeeUser,
  ): void {
    setUserInputMode(mode);
    setUserInputOpen(true);
    setUserInputUid(values?.uid);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading employees export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getCompanyEmployeesExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  function onRegisterUserOpen(): void {
    setRegisterIsOpen(true);
  }

  function onRegisterUserCancel(): void {
    setRegisterIsOpen(false);
  }

  function onRegisterUserFinish(): void {
    setTriggerView(Guid.create().toString());
  }

  function onUserClose(): void {
    setUserInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Employees
        </Typography.Title>
        <Space className="flex flex-row">
          <Typography.Text type="secondary">Show archived</Typography.Text>
          <Switch
            size="small"
            checked={showArchived}
            onChange={setShowArchived}
          />
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("CompanyEmployees", "create")}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("CompanyEmployees", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={onRegisterUserOpen}
          >
            Register Employees
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        {showArchived ? (
          <ArchivedListView<EmployeeUser>
            entityLabel="employee"
            loader={getArchivedEmployees as any}
            restore={restoreEmployee}
            rowKey={(row) => row.uid}
            columns={[
              { title: "User Name", dataIndex: "userName", width: 180 },
              { title: "First Name", dataIndex: "firstName", width: 160 },
              { title: "Last Name", dataIndex: "lastName", width: 160 },
              { title: "Email", dataIndex: "email", width: 220 },
              {
                title: "Deactivated",
                dataIndex: "deactivatedAt",
                width: 160,
                render: (v?: string | null) =>
                  v ? new Date(v).toLocaleString("en-GB") : "—",
              },
            ]}
          />
        ) : (
          <EmployeesView
            trigger={triggerView}
            onSearch={onSearch}
            onAction={(key: string, record: any) =>
              onEmployeeInputOpen(key as any, record)
            }
          />
        )}
      </Col>
      <EmployeeView
        open={userInputOpen}
        mode={userInputMode}
        uid={userInputUid!}
        onFinish={onUserClose}
        onClose={onUserClose}
      />
      <RegisterEmployeesWizard
        open={registerIsOpen}
        onFinish={onRegisterUserFinish}
        onCancel={onRegisterUserCancel}
      />
      {contextHolder}
    </Col>
  );
}

export default EmployeesPage;
